import { useMemo, useState } from 'react';
import {
    Box, Typography, Button, Stack, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress, Alert, Chip, TextField, MenuItem, Tooltip, Divider,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


type AdminPayment = {
    paymentId: string;
    paymentDate: string;
    amount: number;
    currency: string;
    paidByUserId: string | null;
    paidByCompanyId: string | null;
    payerEmail: string | null;
    payerName: string | null;
    code: 'i' | 'o' | 'x' | string;
    provider: string | null;
    providerSessionId: string | null;
    bankDescription: string | null;
    bankTransactionId: string | null;
    hash: string;
    prevHash: string | null;
    deletedAt: string | null;
    createdAt: string;
    createdByUser: string;
};

type AdminPaymentDetail = AdminPayment & {
    deletedByUser: string | null;
    validities: {
        placeValidityId: string;
        placeId: string;
        validFrom: string | null;
        validTill: string | null;
        amount: number;
    }[];
};


export function PaymentsPage() {
    const queryClient = useQueryClient();
    const [filters, setFilters] = useState<{
        from: string; to: string; code: string; provider: string;
    }>({ from: '', to: '', code: '', provider: '' });
    const [adding, setAdding] = useState(false);
    const [detailId, setDetailId] = useState<string | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    const { data: payments, isLoading, error } = useQuery({
        queryKey: ['admin-payments', filters],
        queryFn: async () => {
            const resp = await api.get<{ items: AdminPayment[] }>('/api/v1/admin/payments', {
                params: {
                    from: filters.from || undefined,
                    to: filters.to || undefined,
                    code: filters.code || undefined,
                    provider: filters.provider || undefined,
                },
            });
            return resp.data.items;
        },
    });

    const createMutation = useMutation({
        mutationFn: async (body: Partial<AdminPayment> & { amount: number; code: string }) => {
            await api.post('/api/v1/admin/payments', body);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-payments'] });
            setAdding(false);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (paymentId: string) => {
            await api.delete(`/api/v1/admin/payments/${paymentId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-payments'] });
            setConfirmDeleteId(null);
        },
    });

    const columns = useMemo<GridColDef<AdminPayment>[]>(() => [
        {
            field: 'paymentDate', headerName: 'Datums', width: 150,
            valueFormatter: (v?: string) => v
                ? new Date(v).toLocaleString('lv-LV', { dateStyle: 'short', timeStyle: 'short' })
                : '',
        },
        {
            field: 'amount', headerName: 'Summa', width: 120, align: 'right', headerAlign: 'right',
            renderCell: (p) => (
                <span style={{ color: p.row.code === 'o' ? '#d32f2f' : 'inherit' }}>
                    {p.row.code === 'o' ? '-' : ''}{p.value.toFixed(2)} {p.row.currency.toUpperCase()}
                </span>
            ),
        },
        {
            field: 'code', headerName: 'Kods', width: 80, sortable: false,
            renderCell: (p) => <CodeChip code={p.value as string} />,
        },
        {
            field: 'payer', headerName: 'Maksātājs', flex: 1, minWidth: 200, sortable: false,
            renderCell: (p) => {
                if (!p.row.paidByUserId) return <span style={{ color: '#888' }}>—</span>;
                const name = p.row.payerName;
                const email = p.row.payerEmail;
                if (name && email) return (
                    <Stack sx={{ lineHeight: 1.2, py: 0.5 }}>
                        <span>{name}</span>
                        <span style={{ color: '#888', fontSize: 12 }}>{email}</span>
                    </Stack>
                );
                return name || email || p.row.paidByUserId;
            },
        },
        {
            field: 'provider', headerName: 'PSP', width: 130,
            renderCell: (p) => p.value
                ? <Chip label={p.value} size="small" variant="outlined" />
                : <span style={{ color: '#888' }}>—</span>,
        },
        {
            field: 'hash', headerName: 'Hash', width: 110, sortable: false,
            renderCell: (p) => (
                <Tooltip title={p.value as string}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {(p.value as string).slice(0, 10)}…
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'deletedAt', headerName: '', width: 30, sortable: false,
            renderCell: (p) => p.value
                ? <Tooltip title={`Dzēsts ${new Date(p.value).toLocaleString('lv-LV')}`}>
                      <DeleteIcon fontSize="small" color="disabled" />
                  </Tooltip>
                : null,
        },
        {
            field: 'actions', headerName: '', width: 100, sortable: false, filterable: false,
            renderCell: (p) => (
                <Stack direction="row">
                    <IconButton size="small" title="Detaļas" onClick={() => setDetailId(p.row.paymentId)}>
                        <InfoIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        title="Dzēst (atzīmēt kā kļūdas ierakstu)"
                        color="error"
                        disabled={Boolean(p.row.deletedAt)}
                        onClick={() => setConfirmDeleteId(p.row.paymentId)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ),
        },
    ], []);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4">Maksājumi</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
                    Pievienot
                </Button>
            </Stack>

            {/* Filtri */}
            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
                <TextField
                    size="small" type="date" label="No" InputLabelProps={{ shrink: true }}
                    value={filters.from}
                    onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                    sx={{ width: 160 }}
                />
                <TextField
                    size="small" type="date" label="Līdz" InputLabelProps={{ shrink: true }}
                    value={filters.to}
                    onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                    sx={{ width: 160 }}
                />
                <TextField
                    size="small" select label="Kods"
                    value={filters.code}
                    onChange={(e) => setFilters({ ...filters, code: e.target.value })}
                    sx={{ width: 140 }}
                >
                    <MenuItem value="">Visi</MenuItem>
                    <MenuItem value="i">Ienākošs</MenuItem>
                    <MenuItem value="o">Atmaksāts</MenuItem>
                    <MenuItem value="x">Kļūdas ieraksts</MenuItem>
                </TextField>
                <TextField
                    size="small" label="PSP"
                    placeholder="makecommerce, admin…"
                    value={filters.provider}
                    onChange={(e) => setFilters({ ...filters, provider: e.target.value })}
                    sx={{ width: 200 }}
                />
                <Button onClick={() => setFilters({ from: '', to: '', code: '', provider: '' })}>
                    Notīrīt
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>Neizdevās ielādēt maksājumu sarakstu.</Alert>}

            <Box sx={{ height: 'calc(100vh - 260px)', minHeight: 400, bgcolor: 'background.paper' }}>
                <DataGrid
                    rows={payments || []}
                    columns={columns}
                    getRowId={(r) => r.paymentId}
                    getRowHeight={() => 'auto'}
                    loading={isLoading}
                    disableRowSelectionOnClick
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                />
            </Box>

            <AddPaymentDialog
                open={adding}
                onClose={() => setAdding(false)}
                onSave={(body) => createMutation.mutate(body)}
                isSaving={createMutation.isPending}
            />

            <PaymentDetailDialog
                paymentId={detailId}
                onClose={() => setDetailId(null)}
            />

            <Dialog open={Boolean(confirmDeleteId)} onClose={() => setConfirmDeleteId(null)}>
                <DialogTitle>Dzēst maksājumu?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Maksājums tiks atzīmēts kā <strong>kļūdas ieraksts</strong> (code='x') ar
                        deleted_at/by laukiem. <strong>Hash chain paliks neskarta</strong> -
                        metadati netiek iekļauti kanoniskajā hash payload, tāpēc ķēde turpinās.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteId(null)}>Atcelt</Button>
                    <Button
                        color="error" variant="contained"
                        onClick={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
                        disabled={deleteMutation.isPending}
                    >
                        Dzēst
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}


function CodeChip({ code }: { code: string }) {
    if (code === 'i') return <Chip label="Ienākošs" color="success" size="small" />;
    if (code === 'o') return <Chip label="Atmaksāts" color="warning" size="small" />;
    if (code === 'x') return <Chip label="Kļūdas" size="small" />;
    return <Chip label={code} size="small" />;
}


function AddPaymentDialog({
    open, onClose, onSave, isSaving,
}: {
    open: boolean;
    onClose: () => void;
    onSave: (body: { amount: number; code: string; currency: string; paidByUserId: string | null;
                     bankDescription: string | null; provider: string }) => void;
    isSaving: boolean;
}) {
    const [amount, setAmount] = useState('');
    const [code, setCode] = useState<'i' | 'o' | 'x'>('i');
    const [currency, setCurrency] = useState('eur');
    const [paidByUserId, setPaidByUserId] = useState('');
    const [description, setDescription] = useState('');

    const reset = () => {
        setAmount(''); setCode('i'); setCurrency('eur');
        setPaidByUserId(''); setDescription('');
    };

    return (
        <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth>
            <DialogTitle>Pievienot maksājumu</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Šis pievieno maksājuma ierakstu <strong>bez</strong> place_validity saites.
                        Ja vēlies izsniegt vietas apmaksu - lieto Vietas → 💶 manuālu apmaksu.
                    </Alert>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Summa" type="number" required
                            value={amount} onChange={(e) => setAmount(e.target.value)}
                            inputProps={{ step: '0.01', min: '0.01' }}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label="Valūta" value={currency}
                            onChange={(e) => setCurrency(e.target.value.toLowerCase())}
                            inputProps={{ maxLength: 3 }}
                            sx={{ width: 100 }}
                        />
                        <TextField
                            select label="Kods" value={code}
                            onChange={(e) => setCode(e.target.value as 'i' | 'o' | 'x')}
                            sx={{ width: 160 }}
                        >
                            <MenuItem value="i">Ienākošs</MenuItem>
                            <MenuItem value="o">Atmaksāts</MenuItem>
                            <MenuItem value="x">Kļūdas ieraksts</MenuItem>
                        </TextField>
                    </Stack>
                    <TextField
                        label="Maksātāja Firebase UID"
                        helperText="Atstāj tukšu, ja nav saistīts ar lietotāju"
                        value={paidByUserId}
                        onChange={(e) => setPaidByUserId(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Komentārs / bankas apraksts"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { reset(); onClose(); }}>Atcelt</Button>
                <Button
                    variant="contained"
                    onClick={() => onSave({
                        amount: parseFloat(amount), code, currency,
                        paidByUserId: paidByUserId.trim() || null,
                        bankDescription: description.trim() || null,
                        provider: 'admin',
                    })}
                    disabled={isSaving || !amount || parseFloat(amount) <= 0}
                >
                    {isSaving ? <CircularProgress size={20} /> : 'Saglabāt'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


function PaymentDetailDialog({
    paymentId, onClose,
}: {
    paymentId: string | null;
    onClose: () => void;
}) {
    const { data, isLoading } = useQuery({
        queryKey: ['admin-payment-detail', paymentId],
        queryFn: async () => {
            const resp = await api.get<AdminPaymentDetail>(`/api/v1/admin/payments/${paymentId}`);
            return resp.data;
        },
        enabled: Boolean(paymentId),
    });

    return (
        <Dialog open={Boolean(paymentId)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Maksājuma detaļas</DialogTitle>
            <DialogContent>
                {isLoading && <CircularProgress />}
                {data && (
                    <Stack spacing={2}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 1 }}>
                            <strong>ID:</strong>     <code>{data.paymentId}</code>
                            <strong>Datums:</strong> {new Date(data.paymentDate).toLocaleString('lv-LV')}
                            <strong>Summa:</strong>  {data.amount.toFixed(2)} {data.currency.toUpperCase()}
                            <strong>Kods:</strong>   <Box><CodeChip code={data.code} /></Box>
                            <strong>PSP:</strong>    {data.provider || '—'}
                            <strong>PSP sesija:</strong> <code>{data.providerSessionId || '—'}</code>
                            <strong>Banka:</strong>  {data.bankTransactionId || '—'}
                            <strong>Apraksts:</strong> {data.bankDescription || '—'}
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Maksātājs
                            </Typography>
                            {data.paidByUserId
                                ? (<Stack>
                                    <span>{data.payerName || '—'}</span>
                                    <span style={{ color: '#888', fontSize: 13 }}>
                                        {data.payerEmail} · <code>{data.paidByUserId}</code>
                                    </span>
                                  </Stack>)
                                : <span style={{ color: '#888' }}>Nav saistīts</span>}
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Hash-chain
                            </Typography>
                            <Box sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                                <div><strong>hash:</strong>      {data.hash}</div>
                                <div><strong>prev_hash:</strong> {data.prevHash || '(GENESIS)'}</div>
                            </Box>
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Saistītās vietu validities ({data.validities.length})
                            </Typography>
                            {data.validities.length === 0
                                ? <span style={{ color: '#888' }}>Nav saistītu validity</span>
                                : data.validities.map(v => (
                                    <Box key={v.placeValidityId} sx={{ py: 0.5, fontSize: 13 }}>
                                        <code>{v.placeId}</code> ·{' '}
                                        {v.validFrom ? new Date(v.validFrom).toLocaleDateString('lv-LV') : '?'} —
                                        {v.validTill ? new Date(v.validTill).toLocaleDateString('lv-LV') : '?'}
                                        {' '}· {v.amount.toFixed(2)} EUR
                                    </Box>
                                ))}
                        </Box>
                        {data.deletedAt && (
                            <>
                                <Divider />
                                <Alert severity="warning">
                                    Maksājums dzēsts {new Date(data.deletedAt).toLocaleString('lv-LV')}
                                    {data.deletedByUser ? ` (${data.deletedByUser})` : ''}
                                </Alert>
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Aizvērt</Button>
            </DialogActions>
        </Dialog>
    );
}
